import { useState, useEffect, useRef } from 'react';
import { DialogTitle } from '@headlessui/react';
import { cn } from 'src/utils/cn';
import BaseModal from 'src/components/BaseModal';

interface TableColumnSelectionModalProps {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly onConfirm: (definition: string) => void;
  readonly tableColumnItem: string;
}

const OPERATOR_MAP: Record<string, string> = {
  '>': 'gt',
  '>=': 'gte',
  '<': 'lt',
  '<=': 'lte',
  '==': 'eq',
  '!=': 'neq',
};

const OPERATOR_OPTIONS = Object.keys(OPERATOR_MAP);

type DefinitionOption = 'sum' | 'rowKey' | 'countWhere';

export function TableColumnSelectionModal({
  open,
  onClose,
  onConfirm,
  tableColumnItem,
}: TableColumnSelectionModalProps) {
  const [selectedOption, setSelectedOption] = useState<DefinitionOption | null>(
    'sum',
  );
  const [rowKeyValue, setRowKeyValue] = useState('');
  const [countOperator, setCountOperator] = useState('>');
  const [countValue, setCountValue] = useState('');
  const rowKeyInputRef = useRef<HTMLInputElement>(null);
  const countValueInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (open) {
      setSelectedOption('sum');
      setRowKeyValue('');
      setCountOperator('>');
      setCountValue('');
    }
  }, [open]);

  useEffect(() => {
    if (selectedOption === 'rowKey' && open) {
      const timeoutId = setTimeout(() => rowKeyInputRef.current?.focus(), 0);
      return () => clearTimeout(timeoutId);
    }
    if (selectedOption === 'countWhere' && open) {
      const timeoutId = setTimeout(
        () => countValueInputRef.current?.focus(),
        0,
      );
      return () => clearTimeout(timeoutId);
    }
  }, [selectedOption, open]);

  const getDefinition = (forPreview = false): string => {
    if (!selectedOption) return '';

    if (selectedOption === 'rowKey') {
      return forPreview ? rowKeyValue.trim() || '{RowKey}' : rowKeyValue.trim();
    }

    if (selectedOption === 'countWhere') {
      const opCode = OPERATOR_MAP[countOperator] ?? 'gt';
      const val = countValue.trim();
      if (forPreview) {
        return `count_${opCode}:${val || '{value}'}`;
      }
      return val ? `count_${opCode}:${val}` : '';
    }

    return selectedOption === 'sum' ? 'sum' : '';
  };

  const getPreviewText = (): string => {
    const definition = getDefinition(true);
    return definition ? `[${tableColumnItem}.${definition}]` : '';
  };

  const handleConfirm = (): void => {
    if (!canConfirm()) return;

    const definition = getDefinition(false);
    onConfirm(definition);
    onClose();
  };

  const canConfirm = (): boolean => {
    if (!selectedOption) return false;
    if (selectedOption === 'rowKey') return rowKeyValue.trim().length > 0;
    if (selectedOption === 'countWhere') {
      const val = countValue.trim();
      return val.length > 0 && !Number.isNaN(Number(val));
    }
    return true;
  };

  return (
    <BaseModal open={open} onClose={onClose} size="lg">
      <div>
        <DialogTitle
          as="h3"
          className="text-base font-semibold text-gray-900 mb-4"
        >
          Please choose a definition for this column
        </DialogTitle>

        <div className="space-y-4">
          <div className="space-y-3">
            <label className="flex items-center space-x-3 cursor-pointer">
              <input
                type="radio"
                name="tableColumnDefinition"
                value="sum"
                checked={selectedOption === 'sum'}
                onChange={() => setSelectedOption('sum')}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
              />
              <span className="text-sm font-medium text-gray-900">Sum</span>
            </label>

            <label className="flex items-center space-x-3 cursor-pointer">
              <input
                type="radio"
                name="tableColumnDefinition"
                value="rowKey"
                checked={selectedOption === 'rowKey'}
                onChange={() => setSelectedOption('rowKey')}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
              />
              <span className="text-sm font-medium text-gray-900">Row Key</span>
            </label>

            <label className="flex items-center space-x-3 cursor-pointer">
              <input
                type="radio"
                name="tableColumnDefinition"
                value="countWhere"
                checked={selectedOption === 'countWhere'}
                onChange={() => setSelectedOption('countWhere')}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
              />
              <span className="text-sm font-medium text-gray-900">
                Count Where
              </span>
            </label>
          </div>

          {selectedOption === 'rowKey' && (
            <div>
              <label
                htmlFor="row-key-input-modal"
                className="block text-sm font-medium text-gray-900 mb-2"
              >
                Enter Row Key:
              </label>
              <input
                ref={rowKeyInputRef}
                type="text"
                id="row-key-input-modal"
                name="row-key-input-modal"
                placeholder="Enter Row Key value"
                value={rowKeyValue}
                onChange={(e) => setRowKeyValue(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && canConfirm()) {
                    handleConfirm();
                  }
                }}
                className="flex p-1.5 border border-gray-300 rounded-md min-w-full text-sm outline-1 focus:outline-1 focus:outline-blue-500 -outline-offset-1 outline-gray-300 bg-slate-50"
              />
            </div>
          )}

          {selectedOption === 'countWhere' && (
            <div className="space-y-3 grid grid-cols-2 gap-3 justify-between">
              <div className="col-span-1">
                <label
                  htmlFor="count-operator-select"
                  className="block text-sm font-medium text-gray-900 mb-2"
                >
                  Operator:
                </label>
                <select
                  id="count-operator-select"
                  value={countOperator}
                  onChange={(e) => setCountOperator(e.target.value)}
                  className="block w-full p-1.5 border border-gray-300 rounded-md text-sm outline-1 focus:outline-1 focus:outline-blue-500 -outline-offset-1 outline-gray-300 bg-slate-50"
                >
                  {OPERATOR_OPTIONS.map((op) => (
                    <option key={op} value={op}>
                      {op}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-span-1">
                <label
                  htmlFor="count-value-input"
                  className="block text-sm font-medium text-gray-900 mb-2"
                >
                  Value:
                </label>
                <input
                  ref={countValueInputRef}
                  type="text"
                  id="count-value-input"
                  name="count-value-input"
                  inputMode="decimal"
                  placeholder="Enter numeric value"
                  value={countValue}
                  onChange={(e) => setCountValue(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && canConfirm()) {
                      handleConfirm();
                    }
                  }}
                  className="flex p-1.5 border border-gray-300 rounded-md min-w-full text-sm outline-1 focus:outline-1 focus:outline-blue-500 -outline-offset-1 outline-gray-300 bg-slate-50"
                />
              </div>
            </div>
          )}

          {selectedOption && (
            <div className="mt-4 p-3 bg-gray-50 rounded-md border border-gray-200">
              <p className="text-xs font-medium text-gray-700 mb-1">Preview:</p>
              <p className="text-sm font-mono text-gray-900">
                {getPreviewText()}
              </p>
            </div>
          )}
        </div>

        <div className="mt-6 flex flex-row-reverse gap-3">
          <button
            type="button"
            onClick={handleConfirm}
            disabled={!canConfirm()}
            className={cn(
              'inline-flex justify-center rounded-md px-4 py-2 text-sm font-semibold shadow-sm',
              canConfirm()
                ? 'bg-blue-600 text-white hover:bg-blue-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600'
                : 'bg-gray-300 text-gray-500 cursor-not-allowed',
            )}
          >
            Confirm
          </button>
          <button
            type="button"
            onClick={onClose}
            className="inline-flex justify-center rounded-md bg-white px-4 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-gray-300 ring-inset hover:bg-gray-50"
          >
            Cancel
          </button>
        </div>
      </div>
    </BaseModal>
  );
}
